import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import { useMetricsUi, LegendItem, MONO_FONT } from "./metricsUi";
import { formatCurrency, formatCount, formatCompactCurrency } from "./metricsFormat";
import {
  FALLBACK_SALES_KEY,
  FALLBACK_SOURCE_LABEL,
  allocateSourceSegments,
  collectChartSources,
  formatSharePct,
  getSourceColor,
  leftoverSales,
  sourceSalesDataKey,
} from "./sourceSalesMix";

const ChartTooltip = ({ active, payload, ui, stacked }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <Box
      sx={{
        ...ui.card,
        px: 1.5,
        py: 1.1,
        boxShadow: "0 4px 14px rgba(16,24,40,0.14)",
        minWidth: 180,
      }}
    >
      <Typography
        sx={{ fontSize: 12.5, fontWeight: 700, color: ui.colors.textPrimary }}
      >
        {row.label}
      </Typography>
      <Typography sx={{ fontSize: 11, color: ui.colors.textTertiary, mb: 0.5 }}>
        {row.range}
      </Typography>
      {row.availability === "upcoming" ? (
        <Typography sx={{ fontSize: 12, color: ui.colors.textTertiary }}>
          Upcoming — no sales yet
        </Typography>
      ) : row.availability === "in_progress" && !row.hasRecords ? (
        <Typography sx={{ fontSize: 12, color: ui.colors.textTertiary }}>
          In progress — no sales recorded yet
        </Typography>
      ) : row.hasRecords ? (
        <>
          <Typography
            sx={{
              fontSize: 12,
              color: ui.colors.textSecondary,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            Gross Sales{" "}
            <Box component="span" sx={{ fontWeight: 700, color: ui.colors.textPrimary }}>
              {formatCurrency(row.gross)}
            </Box>
          </Typography>
          {stacked &&
            (row.sourceMix || []).map((part) => (
              <Box
                key={part.source}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.35,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "2px",
                    backgroundColor: part.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 11.5,
                    color: ui.colors.textSecondary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {part.source}{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: ui.colors.textPrimary }}
                  >
                    {formatCurrency(part.sales)}
                  </Box>{" "}
                  ({formatSharePct(part.share)})
                </Typography>
              </Box>
            ))}
          <Typography
            sx={{
              fontSize: 12,
              color: ui.colors.textSecondary,
              fontVariantNumeric: "tabular-nums",
              mt: stacked ? 0.45 : 0,
            }}
          >
            Orders{" "}
            <Box component="span" sx={{ fontWeight: 700, color: ui.colors.textPrimary }}>
              {formatCount(row.orders)}
            </Box>
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: 12, color: ui.colors.textTertiary }}>
          No sales data in this period
        </Typography>
      )}
    </Box>
  );
};

/**
 * Hero chart: Gross Sales (bars) and Orders (line) across seeded fiscal
 * periods, selected period highlighted. Presentation of existing
 * period_history rows only — click a bar to switch periods.
 *
 * When source_breakdown is present, Gross Sales bars are stacked by
 * source. Bar HEIGHT is still the existing period total; colors show mix.
 */
const PeriodTrendChart = ({
  periodHistory = [],
  selectedPeriodId,
  onSelectPeriod,
  title,
  clickHint = "Click a bar to switch period",
  sourceOptions = [],
}) => {
  const ui = useMetricsUi();

  const stackSources = useMemo(
    () => collectChartSources(periodHistory, sourceOptions),
    [periodHistory, sourceOptions],
  );
  const stacked = stackSources.length > 0;

  const data = useMemo(
    () =>
      periodHistory.map((row) => {
        const gross =
          row.availability_status === "upcoming"
            ? null
            : row.gross_sales?.value ?? null;
        const mix = stacked
          ? allocateSourceSegments(row.source_breakdown, gross)
          : [];
        const chartRow = {
          id: row.period_id,
          short: (row.label || row.period_id).split(" ")[0],
          label: row.label,
          range: `${row.start_date} – ${row.end_date}`,
          // Upcoming periods must not plot as zero sales.
          gross,
          orders:
            row.availability_status === "upcoming"
              ? null
              : row.orders?.value ?? null,
          hasRecords: row.has_records,
          availability: row.availability_status,
          sourceMix: mix.map((part) => ({
            ...part,
            color: getSourceColor(part.source, sourceOptions),
          })),
        };
        stackSources.forEach((source) => {
          const part = mix.find((item) => item.source === source);
          chartRow[sourceSalesDataKey(source)] = part ? part.value : 0;
        });
        const leftover = stacked ? leftoverSales(gross, mix) : 0;
        chartRow[FALLBACK_SALES_KEY] = leftover;
        if (leftover > 0) {
          chartRow.sourceMix = [
            ...chartRow.sourceMix,
            {
              source: FALLBACK_SOURCE_LABEL,
              sales: leftover,
              share: leftover / gross,
              value: leftover,
              color: "#94A3B8",
            },
          ];
        }
        return chartRow;
      }),
    [periodHistory, stacked, stackSources, sourceOptions],
  );

  const hasFallback = data.some((row) => row[FALLBACK_SALES_KEY] > 0);
  const multi = stackSources.length + (hasFallback ? 1 : 0) > 1;

  const segmentCell = (entry, color, key) => {
    const selected = entry.id === selectedPeriodId;
    return (
      <Cell
        key={`${entry.id}-${key}`}
        fill={color}
        fillOpacity={selected ? 1 : multi ? 0.72 : 0.22}
        stroke={multi ? "#FFFFFF" : selected ? "none" : ui.borderStrong}
        strokeWidth={multi ? 1 : selected ? 0 : 1}
      />
    );
  };

  const chartTitle =
    title ||
    `Gross Sales & Orders by Fiscal Period (${
      periodHistory[0]?.fiscal_year || "selected year"
    })`;

  // When the chart is a configured section it keeps its slot even with no
  // history, so the page layout does not jump between filters.
  if (!data.length) {
    return (
      <Box
        data-testid="trend-chart-empty"
        sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 2, mb: 3 }}
      >
        <Typography
          sx={{ fontSize: 13.5, fontWeight: 700, color: ui.colors.textPrimary }}
        >
          {chartTitle}
        </Typography>
        <Box
          sx={{
            height: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 13, color: ui.colors.textTertiary }}>
            No sales history for this year
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleBarClick = (entry) => {
    const id = entry?.payload?.id ?? entry?.id;
    if (id) onSelectPeriod?.(id);
  };

  return (
    <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 0.75, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography
          sx={{ fontSize: 13.5, fontWeight: 700, color: ui.colors.textPrimary }}
        >
          {chartTitle}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.75, alignItems: "center", flexWrap: "wrap" }}>
          {stacked
            ? stackSources.map((source) => (
                <LegendItem
                  key={source}
                  color={getSourceColor(source, sourceOptions)}
                  label={source}
                />
              ))
            : <LegendItem color={ui.accent} label="Gross Sales $" />}
          {hasFallback && (
            <LegendItem color="#94A3B8" label={FALLBACK_SOURCE_LABEL} />
          )}
          <LegendItem color={ui.positive} label="Orders" line />
          <Typography sx={{ fontSize: 11, color: ui.colors.textTertiary }}>
            {clickHint}
          </Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
          <CartesianGrid
            stroke={ui.colors.gridLine}
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="short"
            tickLine={false}
            axisLine={{ stroke: ui.colors.border }}
            tick={{
              fill: ui.colors.textTertiary,
              fontSize: 11,
              fontFamily: MONO_FONT,
            }}
          />
          <YAxis
            yAxisId="sales"
            tickFormatter={formatCompactCurrency}
            tickLine={false}
            axisLine={false}
            tick={{
              fill: ui.colors.textTertiary,
              fontSize: 11,
              fontFamily: MONO_FONT,
            }}
            width={44}
          />
          <YAxis
            yAxisId="orders"
            orientation="right"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tick={{
              fill: ui.colors.textTertiary,
              fontSize: 11,
              fontFamily: MONO_FONT,
            }}
            width={40}
          />
          <RechartsTooltip
            cursor={{ fill: ui.surfaceAlt, opacity: 0.6 }}
            content={<ChartTooltip ui={ui} stacked={stacked} />}
          />
          {stacked
            ? [
                ...stackSources.map((source, index) => {
                  const color = getSourceColor(source, sourceOptions);
                  const isLast = !hasFallback && index === stackSources.length - 1;
                  return (
                    <Bar
                      key={source}
                      yAxisId="sales"
                      dataKey={sourceSalesDataKey(source)}
                      name={source}
                      stackId="gross"
                      fill={color}
                      maxBarSize={64}
                      radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      onClick={handleBarClick}
                      cursor="pointer"
                    >
                      {data.map((entry) => segmentCell(entry, color, source))}
                    </Bar>
                  );
                }),
                hasFallback ? (
                  <Bar
                    key={FALLBACK_SALES_KEY}
                    yAxisId="sales"
                    dataKey={FALLBACK_SALES_KEY}
                    name={FALLBACK_SOURCE_LABEL}
                    stackId="gross"
                    fill="#94A3B8"
                    maxBarSize={64}
                    radius={[4, 4, 0, 0]}
                    onClick={handleBarClick}
                    cursor="pointer"
                  >
                    {data.map((entry) =>
                      segmentCell(entry, "#94A3B8", FALLBACK_SALES_KEY),
                    )}
                  </Bar>
                ) : null,
              ]
            : (
              <Bar
                yAxisId="sales"
                dataKey="gross"
                name="Gross Sales"
                radius={[4, 4, 0, 0]}
                maxBarSize={64}
                onClick={handleBarClick}
                cursor="pointer"
              >
                {data.map((entry) => (
                  <Cell
                    key={entry.id}
                    fill={entry.id === selectedPeriodId ? ui.accent : ui.accentSoft}
                    stroke={
                      entry.id === selectedPeriodId ? "none" : ui.borderStrong
                    }
                    strokeWidth={entry.id === selectedPeriodId ? 0 : 1}
                  />
                ))}
              </Bar>
            )}
          <Line
            yAxisId="orders"
            type="monotone"
            dataKey="orders"
            name="Orders"
            stroke={ui.positive}
            strokeWidth={2}
            dot={{ r: 3, fill: ui.positive, strokeWidth: 0 }}
            activeDot={{ r: 4.5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PeriodTrendChart;
